import { useMemo, useState } from 'react';
import { Checkbox, Field, Input, RadioGroup, Select } from '@bb/ui';
import { ISO_WEEKDAYS } from '../schedule/scheduledDelivery.ts';
import {
  crossesMidnight,
  DEFAULT_TIME_FRAME,
  issuesAt,
  MAX_CANCEL_EVENTS,
  MAX_TRIGGERS,
  switchDelayKind,
  toSeconds,
  UNIT_SECONDS,
  unitOf,
  validateActionBased,
  withDefaults,
  type ActionBasedDelivery,
  type ActionBasedIssue,
  type DelayKind,
  type DelayUnit,
  type TimeFrame,
  type TriggerDelay,
} from './actionBased.ts';
import { NumberInput } from './NumberInput.tsx';
import { TriggerList } from './TriggerEditor.tsx';

export { validateActionBased } from './actionBased.ts';
export type { ActionBasedIssue, ActionBasedValidation } from './actionBased.ts';

export interface ActionBasedFormProps {
  environmentId: string;
  /** `content.schedule.actionBased` of the draft; no value yet reads as one custom event to choose, sent immediately. */
  value: ActionBasedDelivery | undefined;
  /** Every edit, valid or not: the form shows what the contract rejects (see `validateActionBased`). */
  onChange: (next: ActionBasedDelivery) => void;
  /** A launched revision: nothing can change. */
  readOnly?: boolean;
}

const DELAY_KINDS: Array<{ value: DelayKind; label: string }> = [
  { value: 'none', label: 'Immediately' },
  { value: 'after', label: 'After a delay' },
  { value: 'next_weekday', label: 'On the next chosen weekday' },
  { value: 'calendar_days', label: 'A number of calendar days later' },
];
const UNITS: Array<{ value: DelayUnit; label: string }> = [
  { value: 'minutes', label: 'minutes' },
  { value: 'hours', label: 'hours' },
  { value: 'days', label: 'days' },
];
const WEEKDAYS = ISO_WEEKDAYS.map((d) => ({ value: String(d.day), label: d.label }));
const OUTSIDE = [
  { value: 'next', label: 'Send at the next available time' },
  { value: 'drop', label: 'Drop the send' },
];

function Errors({ messages }: { messages: string[] }) {
  return (
    <>
      {messages.map((message) => (
        <p key={message} className="bb-field__error" role="alert">
          {message}
        </p>
      ))}
    </>
  );
}

function LocalTime({ checked, readOnly, onChange }: { checked: boolean; readOnly: boolean; onChange: (on: boolean) => void }) {
  return (
    <div>
      <Checkbox label="In users' local time zone" checked={checked} disabled={readOnly} onCheckedChange={onChange} />
      <p className="hint">Without it, the time is in the environment time zone.</p>
    </div>
  );
}

/** «After N minutes / hours / days»: the unit stays as chosen, the value is stored in seconds. */
function AfterDelay({ seconds, error, readOnly, onChange }: { seconds: number; error: string | undefined; readOnly: boolean; onChange: (seconds: number) => void }) {
  const [unit, setUnit] = useState<DelayUnit>(() => unitOf(seconds));
  const amount = seconds > 0 ? seconds / UNIT_SECONDS[unit] : undefined;
  return (
    <div className="audience-filter">
      <Field label="Wait" error={error}>
        <NumberInput min={1} step={1} value={amount} disabled={readOnly} onValueChange={(n) => onChange(n === undefined ? 0 : toSeconds(n, unit))} />
      </Field>
      <Field label="Unit">
        <Select
          options={UNITS}
          value={unit}
          disabled={readOnly}
          onChange={(e) => {
            const next = e.target.value as DelayUnit;
            setUnit(next);
            if (amount !== undefined) onChange(toSeconds(amount, next));
          }}
        />
      </Field>
    </div>
  );
}

function DelayFields({ delay, issues, readOnly, onChange }: { delay: TriggerDelay; issues: readonly ActionBasedIssue[]; readOnly: boolean; onChange: (next: TriggerDelay) => void }) {
  const err = (field: string) => issuesAt(issues, 'delay', field)[0];
  return (
    <fieldset className="audience-group">
      <legend>Delay</legend>
      <RadioGroup label="When to send after the trigger" options={DELAY_KINDS.map((k) => ({ ...k, disabled: readOnly }))} value={delay.kind} onValueChange={(kind) => onChange(switchDelayKind(delay, kind as DelayKind))} />
      {delay.kind === 'after' && <AfterDelay seconds={delay.seconds} error={err('seconds')} readOnly={readOnly} onChange={(seconds) => onChange({ kind: 'after', seconds })} />}
      {delay.kind === 'next_weekday' && (
        <>
          <Field label="Weekday" hint="Never the same day: a trigger on a Monday with Monday chosen sends a week later." error={err('weekday')}>
            <Select options={WEEKDAYS} value={String(delay.weekday)} disabled={readOnly} onChange={(e) => onChange({ ...delay, weekday: Number(e.target.value) })} />
          </Field>
          <Field label="Time" error={err('time')}>
            <Input type="time" value={delay.time} disabled={readOnly} onChange={(e) => onChange({ ...delay, time: e.target.value })} />
          </Field>
          <LocalTime checked={delay.localTime} readOnly={readOnly} onChange={(localTime) => onChange({ ...delay, localTime })} />
        </>
      )}
      {delay.kind === 'calendar_days' && (
        <>
          <Field label="Days later" error={err('days')}>
            <NumberInput min={1} max={90} step={1} value={delay.days > 0 ? delay.days : undefined} disabled={readOnly} onValueChange={(days) => onChange({ ...delay, days: days ?? 0 })} />
          </Field>
          <Field label="Time" error={err('time')}>
            <Input type="time" value={delay.time} disabled={readOnly} onChange={(e) => onChange({ ...delay, time: e.target.value })} />
          </Field>
          <LocalTime checked={delay.localTime} readOnly={readOnly} onChange={(localTime) => onChange({ ...delay, localTime })} />
        </>
      )}
      <Errors messages={issuesAt(issues, 'delay')} />
    </fieldset>
  );
}

function TimeFrameFields({ frame, issues, readOnly, onChange }: { frame: TimeFrame | undefined; issues: readonly ActionBasedIssue[]; readOnly: boolean; onChange: (next: TimeFrame | undefined) => void }) {
  const err = (field: string) => issuesAt(issues, 'timeFrame', field)[0];
  return (
    <fieldset className="audience-group">
      <legend>Time frame</legend>
      <Checkbox label="Send only at certain hours" checked={frame !== undefined} disabled={readOnly} onCheckedChange={(on) => onChange(on ? DEFAULT_TIME_FRAME : undefined)} />
      {frame && (
        <>
          <div className="audience-filter">
            <Field label="From" error={err('from')}>
              <Input type="time" value={frame.from} disabled={readOnly} onChange={(e) => onChange({ ...frame, from: e.target.value })} />
            </Field>
            <Field label="To" error={err('to')}>
              <Input type="time" value={frame.to} disabled={readOnly} onChange={(e) => onChange({ ...frame, to: e.target.value })} />
            </Field>
          </div>
          {crossesMidnight(frame) && (
            <p className="hint">
              Runs over midnight: sends from {frame.from} until {frame.to} the next day.
            </p>
          )}
          <LocalTime checked={frame.localTime} readOnly={readOnly} onChange={(localTime) => onChange({ ...frame, localTime })} />
          <RadioGroup
            label="Outside these hours"
            options={OUTSIDE.map((o) => ({ ...o, disabled: readOnly }))}
            value={frame.sendAtNextAvailable ? 'next' : 'drop'}
            onValueChange={(choice) => onChange({ ...frame, sendAtNextAvailable: choice === 'next' })}
          />
        </>
      )}
      <Errors messages={issuesAt(issues, 'timeFrame')} />
    </fieldset>
  );
}

/**
 * Action-based delivery of a campaign (BB-100, CMP-003/005): what the user does that sends it (any of up to ten
 * triggers), how long to wait, what cancels a waiting send, the hours when sending is allowed and whether the audience
 * is checked again at send time. Controlled: every edit is emitted, and what the contract rejects is shown inline.
 * The campaign window (`schedule.startAt` / `endAt`) belongs to the wizard.
 */
export function ActionBasedForm({ environmentId, value, onChange, readOnly = false }: ActionBasedFormProps) {
  const current = useMemo(() => withDefaults(value), [value]);
  const issues = useMemo(() => validateActionBased(current).issues, [current]);
  const set = (patch: Partial<ActionBasedDelivery>) => onChange({ ...current, ...patch });
  const delayed = current.delay.kind !== 'none';

  return (
    <section className="audience-builder" aria-label="Action-based delivery">
      <fieldset className="audience-group">
        <legend>Send when the user does any of these</legend>
        <TriggerList
          environmentId={environmentId}
          readOnly={readOnly}
          issues={issues}
          list="triggers"
          items={current.triggers}
          onChange={(triggers) => set({ triggers })}
          itemLabel="Trigger"
          addLabel="Add a trigger"
          min={1}
          max={MAX_TRIGGERS}
        />
      </fieldset>

      <DelayFields delay={current.delay} issues={issues} readOnly={readOnly} onChange={(delay) => set({ delay })} />

      <fieldset className="audience-group">
        <legend>Cancel on</legend>
        <p className="hint">A send still waiting for its delay is cancelled when the user does any of these.</p>
        {!delayed && current.cancelOn.length > 0 && <p className="notice notice--neutral">Without a delay no send waits, so these never cancel one.</p>}
        <TriggerList
          environmentId={environmentId}
          readOnly={readOnly}
          issues={issues}
          list="cancelOn"
          items={current.cancelOn}
          onChange={(cancelOn) => set({ cancelOn })}
          itemLabel="Cancel event"
          addLabel="Add a cancel event"
          min={0}
          max={MAX_CANCEL_EVENTS}
        />
      </fieldset>

      <TimeFrameFields
        frame={current.timeFrame}
        issues={issues}
        readOnly={readOnly}
        onChange={(timeFrame) => {
          const { timeFrame: _old, ...rest } = current;
          onChange(timeFrame ? { ...rest, timeFrame } : rest);
        }}
      />

      <div>
        <Checkbox label="Check the audience again when the delayed send goes out" checked={current.reevaluateAudienceAtSend} disabled={readOnly} onCheckedChange={(reevaluateAudienceAtSend) => set({ reevaluateAudienceAtSend })} />
        <p className="hint">{delayed ? 'Without it, the audience is checked only when the trigger happens.' : 'Only matters with a delay: without one, the audience is checked when the trigger happens.'}</p>
      </div>

      <Errors messages={issuesAt(issues)} />
      {readOnly && <p className="hint">This revision is launched: its triggers cannot change.</p>}
    </section>
  );
}
